import React from 'react';
import { useDropzone } from 'react-dropzone';
import uploadIcon from '../../../assets/icons/upload.svg';
import pdfIcon from '../../../assets/images/pdfIcons.svg';
import docIcon from '../../../assets/images/docIcon.svg';
import selected from '../../../assets/images/select-box-circle-fill.svg';
import deleteIcon from '../../../assets/icons/delete.svg';

const DropZone = ({
  name,
  onChange,
  value = [],
  condition = true,
  height,
  multiple = true,
}) => {
  const [files, setFiles] = React.useState(value);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    setFiles(value || []);
  }, [value]);

  const onDrop = React.useCallback(
    (acceptedFiles, rejectedFiles) => {
      const sizeError = acceptedFiles.filter(
        (file) => file.size > 50 * 1024 * 1024
      ); // 50MB limit
      if (sizeError.length > 0) {
        setError('File size should not exceed 50MB');
        return;
      }

      setError(''); // Clear error if no issue with file size

      setFiles((prevFiles) => {
        let updatedFiles;

        if (!multiple) {
          // If multiple is false, only keep the last file
          updatedFiles = acceptedFiles;
        } else {
          // Allow multiple files
          const newFiles = acceptedFiles.filter(
            (file) =>
              !prevFiles.some(
                (prevFile) =>
                  prevFile.name === file.name || prevFile.path === file.path
              )
          );
          updatedFiles = [...prevFiles, ...newFiles];
        }

        if (onChange) onChange(updatedFiles); // Notify parent
        return updatedFiles;
      });
    },
    [onChange, multiple]
  );

  const handleDelete = (fileName) => {
    setFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter((file) => file.path !== fileName);
      if (onChange) onChange(updatedFiles); // Notify parent
      return updatedFiles;
    });
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/pdf': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        [],
    },
    onDrop,
    multiple,
  });

  React.useEffect(() => {
    if (onChange) {
      onChange(files); // Sync updated files with the parent
    }
  }, [files]);

  return (
    <div>
      <section
        className={`border-[1px] border-dashed rounded-[12px] border-[#CDD0D5] ${condition ? 'h-[138px]' : `h-[${height}]`} relative group`}
      >
        <div {...getRootProps({ className: 'dropzone' })}>
          <input {...getInputProps()} />
          <div className="flex justify-center">
            <div className='className="flex flex-col items-center justify-center text-center mb-12'>
              <div className="m-0 auto pt-8">
                <img src={uploadIcon} alt="logo" className="m-0 mx-auto" />
              </div>
              <p className="font-medium leading-[20px] text-[14px] text-[#0A0D14] pt-2">
                {multiple
                  ? 'Choose files or drag & drop them here.'
                  : 'Choose a file or drag & drop it here.'}
              </p>
              <p className="font-[400] leading-[16px] text-[12px] text-[#868C98]">
                System only accepts PDF (.pdf) and Word (.doc, .docx) file types
                for uploads.
              </p>
            </div>
          </div>
        </div>
        <div className="absolute bottom-[150%] left-1/2 transform -translate-x-1/2 hidden group-hover:flex flex-col items-center">
          <div className="bg-[#0A0D14] text-white text-xs rounded px-2 py-1">
            Upload up to 15 documents per section, each under 50 MB.
          </div>
          <div className="h-2 w-2 bg-[#0A0D14] rotate-45 transform origin-bottom"></div>
        </div>
      </section>
      <div className="text-red-500 text-[12px]">{error}</div>

      <div className="grid grid-cols-1 md:grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-5">
        {files?.map((file) => {
          const filePath = file?.path || file;
          const fileExtension = filePath
            ?.split?.('.')
            ?.pop?.()
            ?.toLowerCase?.();

          let fileIcon = null;
          if (fileExtension === 'pdf') fileIcon = pdfIcon;
          else if (fileExtension === 'doc' || fileExtension === 'docx')
            fileIcon = docIcon;

          return (
            <div
              key={file?.path}
              className="flex items-center space-x-2 border border-[#E2E4E9] w-full h-[72px] rounded-[12px] p-3"
            >
              {fileIcon && (
                <img
                  src={fileIcon}
                  alt="file icon"
                  className="w-[40px] h-[40px] flex-shrink-0"
                />
              )}
              <div className="flex flex-col w-full">
                <div className="flex justify-between items-center w-full">
                  <div className="font-medium leading-[20px] text-[14px] text-[#0A0D14] truncate max-w-[150px]">
                    {filePath}
                  </div>
                  <img
                    src={deleteIcon}
                    alt="delete icon"
                    className="w-[20px] h-[20px] cursor-pointer flex-shrink-0"
                    onClick={() => handleDelete(filePath)}
                  />
                </div>
                <div className="flex justify-between items-center text-[12px] text-[#868C98] pt-1">
                  {file?.size ? (
                    <span className="truncate">
                      {(file?.size / 1024)?.toFixed(2)} KB
                    </span>
                  ) : (
                    <span></span>
                  )}
                  <span className="flex items-center">
                    <img
                      src={selected}
                      alt="logo"
                      className="w-[16px] h-[16px]"
                    />
                    <span className="ml-1 text-[#0A0D14]">Completed</span>
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DropZone;
